import { useEffect, useState } from "react";
import { Mail, UserCheck } from "lucide-react";
import { api } from "../../lib/api";
import { fmtDateMtl } from "../../lib/dateMtl";

// Carte « Visiteurs identifiés » de l'aperçu admin : clients CONNECTÉS et TOUTES
// leurs pages vues, triés par intérêt — pour relancer en priorité ceux qui
// reviennent souvent sans avoir encore commandé. Période au choix (aujourd'hui
// à 1 an), comme les autres cartes de statistiques.
// [jours, décalage (0 = se termine aujourd'hui, 1 = hier), libellé]
const RANGES = [[1, 0, "Aujourd'hui"], [1, 1, "Hier"], [7, 0, "7 j"], [15, 0, "15 j"],
                [30, 0, "30 j"], [90, 0, "90 j"], [180, 0, "6 mois"], [365, 0, "1 an"]];
const periodLabel = (r, o) =>
  o === 1 && r === 1 ? "hier" : r === 1 ? "aujourd'hui"
  : r === 365 ? "1 an" : r === 180 ? "6 mois" : `${r} derniers jours`;

export default function EngagedVisitors() {
  const [data, setData] = useState(null);
  const [range, setRange] = useState(30);
  const [offset, setOffset] = useState(0);  // 1 = « Hier »

  useEffect(() => {
    let alive = true;
    setData(null);
    api.get("/admin/visitors/engagement", { params: { days: range, offset } })
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData(false); });
    return () => { alive = false; };
  }, [range, offset]);

  if (data === false) return null;
  const visitors = data?.visitors || [];
  const shortDate = (ts) => fmtDateMtl(ts).split("-").reverse().join("/");  // date à Montréal

  return (
    <div className="mt-8 bg-surface border border-gold/15 rounded-xl p-7" data-testid="engaged-visitors">
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <UserCheck className="w-4 h-4 text-gold" />
        <h3 className="text-xs uppercase tracking-widest text-ivory/70">
          Visiteurs identifiés — {periodLabel(range, offset)}
        </h3>
        <span className="text-xs text-ivory/55" data-testid="engaged-count">
          {visitors.length} client{visitors.length > 1 ? "s" : ""} connecté{visitors.length > 1 ? "s" : ""}
        </span>
        <div className="ml-auto flex flex-wrap gap-1.5" data-testid="engaged-range-picker">
          {RANGES.map(([d, o, lab]) => (
            <button
              key={`${d}-${o}`}
              data-testid={o ? "engaged-range-hier" : `engaged-range-${d}`}
              onClick={() => { setRange(d); setOffset(o); }}
              className={`px-3 py-1 rounded-full border text-[11px] transition-colors duration-300 ${
                range === d && offset === o
                  ? "bg-gold text-ink border-gold font-bold"
                  : "border-ivory/20 text-ivory/60 hover:border-gold/50 hover:text-gold"
              }`}
            >
              {lab}
            </button>
          ))}
        </div>
      </div>

      {!data ? (
        <p className="text-sm text-ivory/50">Chargement…</p>
      ) : visitors.length === 0 ? (
        <p className="text-sm text-ivory/60" data-testid="engaged-empty">
          Aucun client connecté n'a encore navigué — les pages vues des visiteurs
          connectés s'afficheront ici dès leur prochaine visite.
        </p>
      ) : (
        <ul className="space-y-3">
          {visitors.map((v, i) => (
            <li key={v.email} data-testid={`engaged-visitor-${i}`}
              className="flex flex-wrap items-center gap-3 border-b border-ivory/5 pb-3 last:border-0 last:pb-0">
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-semibold text-gold" data-testid={`engaged-name-${i}`}>{v.name || v.email}</span>
                  <span className="text-xs text-ivory/55">{v.email}</span>
                </span>
                <span className="flex flex-wrap gap-1.5 mt-1.5">
                  {(v.pages || []).map((p) => (
                    <span key={p.path} className="text-[10px] font-mono bg-ink border border-ivory/10 text-ivory/70 rounded-full px-2 py-0.5"
                      title={`${p.n} vue${p.n > 1 ? "s" : ""}`}>
                      {p.path} · {p.n}
                    </span>
                  ))}
                </span>
              </span>
              <span className="text-right shrink-0">
                <span className="block font-serif text-xl text-champagne" data-testid={`engaged-views-${i}`}>{v.views}</span>
                <span className="block text-[10px] text-ivory/50">pages · dernière {shortDate(v.last)}</span>
              </span>
              <a href={`mailto:${v.email}?subject=${encodeURIComponent("Votre visite chez Mr Trophée")}`}
                data-testid={`engaged-mailto-${i}`}
                title="Relancer ce client par courriel"
                className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-full border border-gold/40 text-gold hover:bg-gold hover:text-ink transition-colors duration-300">
                <Mail className="w-4 h-4" />
              </a>
            </li>
          ))}
        </ul>
      )}
      <p className="text-[11px] text-ivory/55 mt-4">
        Seuls les visiteurs connectés à leur compte sont identifiés · vos propres visites (admin) ne sont pas comptées.
      </p>
    </div>
  );
}
